/**
 * Log one submission (a transmittal) for several drawings at once.
 *  - From the All Drawings bulk bar ("Log submission"): for the selected drawings.
 *  - From the Submission Log ("Log transmittal", wave 2 #8): pick the drawings here — search, one
 *    project at a time, tick a whole project.
 * Real date picker, sender prefilled with the signed-in user, "Sent to" remembered per project in this
 * browser, one undo step (POST /shop-drawings/bulk-submissions).
 */
import { useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getErrorMessage } from '@/lib/errors';
import { formatNumber } from '@/lib/format';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { cn } from '@/lib/utils';
import { REPLIES, REPLY_LABELS, STATUS_LABELS, drawingTitle, labelize, mostCommon, projectNumber, realDrawingId, toDayFirst, todayIso, valueKey } from './model';
import { DisciplineChip, DrawingIdText, SdCheckbox } from './SdUi';

const REPLY_EFFECT = {
  PENDING: 'Drawings keep their status until the consultant replies.',
  APPROVED: 'Drawings become Completed.',
  APPROVED_WITH_COMMENTS: 'Drawings become Completed w/ remarks.',
  REVISE_AND_RESUBMIT: 'Drawings go back to In progress.',
  REJECTED: 'Drawings go back to In progress.',
};

// The dialog's one focus style (navy border + soft ring), also on the picker's project select and search.
const focusClass = 'focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)] focus-visible:outline-none';
const inputClass = `mt-1 h-9 w-full rounded-control border border-line bg-surface px-2.5 text-[13px] font-normal text-ink outline-none placeholder:text-ink-4 ${focusClass}`;
const labelClass = 'block text-[12px] font-semibold text-ink-2';
const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const PICK_LIMIT = 200;

const sentToKey = (projectKey) => storageKey('sd', 'sentTo', projectKey || 'none');
/** 'Sent to' last used for a project in this browser (else 'Consultant'). */
export const rememberedSentTo = (drawings) => {
  const projects = [...new Set(drawings.map((drawing) => valueKey(drawing.project)))];
  if (projects.length !== 1) return 'Consultant';
  return readStored(sentToKey(projects[0]), '') || 'Consultant';
};

/**
 * Second line of a picker row: with "All projects" the project and the Drawing ID come first, so two
 * "ROOF FLOOR - DDC" of different projects can be told apart; then the block / floor and the status.
 */
function CandidateLine({ drawing, showProject }) {
  const title = drawingTitle(drawing);
  const number = projectNumber(drawing);
  const place = [drawing.block, drawing.floor].map((value) => String(value || '').trim()).filter((value) => value && value !== '-' && value !== title).join(' · ');
  const id = realDrawingId(drawing.drawingId);
  const rest = [place, labelize(drawing.status, STATUS_LABELS)].filter(Boolean).join(' · ');
  return (
    <span className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[11px] text-ink-3">
      {showProject ? (
        <>
          <span className="max-w-[45%] shrink-0 truncate font-semibold text-ink-2" title={drawing.project || 'No project'}>
            {drawing.project || 'No project'}{number ? <span className="font-mono font-medium text-ink-3"> #{number}</span> : null}
          </span>
          <span className="shrink-0" aria-hidden="true">·</span>
          {id ? <DrawingIdText value={id} keepEnd className="min-w-0 max-w-[40%] shrink text-[11px]" /> : <span className="shrink-0 italic">No ID</span>}
        </>
      ) : id ? <DrawingIdText value={id} keepEnd className="min-w-0 max-w-[55%] shrink text-[11px]" /> : null}
      {(showProject || id) && rest ? <span className="shrink-0" aria-hidden="true">·</span> : null}
      <span className="min-w-0 truncate">{rest}</span>
    </span>
  );
}

const emptyForm = (by, drawings) => ({ to: rememberedSentTo(drawings), date: todayIso(), rev: '', pdf: '', reply: 'PENDING', comments: '', by });

export function BulkSubmissionDialog({ open, onOpenChange, drawings, currentUserName, onSave, pickFrom = null }) {
  const picking = Array.isArray(pickFrom);
  const [form, setForm] = useState(() => emptyForm(currentUserName, drawings));
  const [picked, setPicked] = useState(() => new Set(drawings.map((drawing) => drawing.id)));
  const [project, setProject] = useState('');
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setForm(emptyForm(currentUserName, drawings));
      setPicked(new Set(drawings.map((drawing) => drawing.id)));
      setProject(picking && drawings.length ? valueKey(drawings[0].project) : '');
      setQuery('');
      setError('');
    }
  }
  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const projectOptions = useMemo(() => {
    if (!picking) return [];
    const map = new Map();
    pickFrom.forEach((drawing) => {
      const key = valueKey(drawing.project) || '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(drawing);
    });
    return [...map.entries()]
      .map(([key, list]) => ({ key, label: mostCommon(list, (drawing) => drawing.project) || 'No project', count: list.length }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [picking, pickFrom]);

  const candidates = useMemo(() => {
    if (!picking) return [];
    const needle = query.trim().toLowerCase();
    return pickFrom.filter((drawing) => (
      (!project || (valueKey(drawing.project) || '__none__') === project)
      && (!needle || [drawingTitle(drawing), drawing.drawingId, drawing.block, drawing.floor, drawing.id, drawing.disc].join(' ').toLowerCase().includes(needle))
    ));
  }, [picking, pickFrom, project, query]);

  const chosen = picking ? pickFrom.filter((drawing) => picked.has(drawing.id)) : drawings;
  const names = chosen.slice(0, 3).map(drawingTitle);
  const more = chosen.length - names.length;

  const onProjectChange = (key) => {
    setProject(key);
    const list = pickFrom.filter((drawing) => (valueKey(drawing.project) || '__none__') === key);
    setForm((current) => ({ ...current, to: current.to && current.to !== 'Consultant' ? current.to : rememberedSentTo(list) }));
  };

  const toggle = (ids, checked) => setPicked((current) => {
    const next = new Set(current);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });

  const submit = async (event) => {
    event.preventDefault();
    if (!chosen.length) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ ...form, date: toDayFirst(form.date) }, chosen.map((drawing) => drawing.id));
      [...new Set(chosen.map((drawing) => valueKey(drawing.project)))].forEach((key) => writeStored(sentToKey(key), form.to.trim()));
      onOpenChange(false);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not log the submission'));
    } finally {
      setSaving(false);
    }
  };

  const visibleCandidates = candidates.slice(0, PICK_LIMIT);
  const allVisiblePicked = visibleCandidates.length > 0 && visibleCandidates.every((drawing) => picked.has(drawing.id));

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent className={picking ? 'max-h-[92vh] max-w-3xl overflow-hidden rounded-2xl border-line bg-surface p-0 text-ink' : 'max-w-lg rounded-2xl border-line bg-surface p-0 text-ink'}>
        <form onSubmit={submit} className={picking ? 'flex max-h-[92vh] flex-col' : undefined}>
          <DialogHeader className="border-b border-line-2 px-5 pb-3 pt-5 text-left">
            <DialogTitle className="font-heading text-[17px] font-bold text-ink">
              {picking ? 'Log transmittal' : `Log submission for ${chosen.length} ${chosen.length === 1 ? 'drawing' : 'drawings'}`}
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-ink-3">
              {picking
                ? 'Everything sent together in one email or letter: pick the drawings, then say where and when it went.'
                : <>{names.join(', ')}{more > 0 ? ` and ${more} more` : ''}</>}
            </DialogDescription>
          </DialogHeader>
          {/* minmax(0, …) + min-w-0: long drawing names truncate in the list instead of widening
              the left track and squeezing the form. */}
          <div className={picking ? 'grid min-h-0 flex-1 gap-0 overflow-y-auto md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] md:overflow-hidden' : undefined}>
            {picking ? (
              <div className="flex min-h-0 min-w-0 flex-col border-b border-line-2 md:border-b-0 md:border-r">
                <div className="flex flex-wrap items-center gap-2 px-4 pb-2 pt-3">
                  <select
                    value={project}
                    onChange={(event) => onProjectChange(event.target.value)}
                    className={cn('h-8 min-w-0 flex-1 rounded-control border border-line bg-surface px-2 text-[12.5px] font-semibold text-ink outline-none', focusClass)}
                    aria-label="Project"
                    data-testid="transmittal-project"
                  >
                    <option value="">All projects</option>
                    {projectOptions.map((option) => <option key={option.key} value={option.key}>{option.label} ({fmt(option.count)})</option>)}
                  </select>
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Find drawing, ID, floor…"
                    aria-label="Find drawings"
                    className={cn('h-8 min-w-0 flex-1 rounded-control border border-line bg-surface px-2.5 text-[12.5px] text-ink outline-none placeholder:text-ink-4', focusClass)}
                    data-testid="transmittal-search"
                  />
                </div>
                <label className="flex items-center gap-2 border-b border-line-2 px-4 pb-2 text-[12px] font-semibold text-ink-2">
                  <SdCheckbox
                    checked={allVisiblePicked}
                    indeterminate={!allVisiblePicked && visibleCandidates.some((drawing) => picked.has(drawing.id))}
                    onCheckedChange={(checked) => toggle(visibleCandidates.map((drawing) => drawing.id), Boolean(checked))}
                    disabled={!visibleCandidates.length}
                    aria-label="Tick every drawing in the list"
                  />
                  {fmt(candidates.length)} {candidates.length === 1 ? 'drawing' : 'drawings'}{candidates.length > PICK_LIMIT ? ` (first ${PICK_LIMIT} shown — pick a project or search)` : ''}
                </label>
                <ul className="min-h-[160px] flex-1 overflow-y-auto py-1 md:max-h-[52vh]" data-testid="transmittal-candidates">
                  {visibleCandidates.map((drawing) => (
                    <li key={drawing.id}>
                      <label className="flex cursor-pointer items-center gap-2.5 px-4 py-1.5 text-[12.5px] hover:bg-surface-hover">
                        <SdCheckbox checked={picked.has(drawing.id)} onCheckedChange={(checked) => toggle([drawing.id], Boolean(checked))} aria-label={`Pick ${drawingTitle(drawing)}`} data-testid={`transmittal-pick-${drawing.id}`} />
                        <DisciplineChip disc={drawing.disc} className="h-[18px] min-w-[32px] rounded-[5px] px-1 text-[9.5px]" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-semibold text-ink">{drawingTitle(drawing)}</span>
                          <CandidateLine drawing={drawing} showProject={!project} />
                        </span>
                      </label>
                    </li>
                  ))}
                  {!visibleCandidates.length ? <li className="px-4 py-6 text-center text-[12.5px] text-ink-3">No drawings match.</li> : null}
                </ul>
              </div>
            ) : null}
            <div className={picking ? 'grid min-w-0 content-start gap-3 overflow-y-auto px-5 py-4 sm:grid-cols-2' : 'grid gap-3 px-5 py-4 sm:grid-cols-2'}>
              <label className={labelClass}>
                Sent to
                <input className={inputClass} value={form.to} onChange={update('to')} required data-testid="bulk-submission-to" />
              </label>
              <label className={labelClass}>
                Date sent
                <input type="date" className={inputClass} value={form.date} max={todayIso()} onChange={update('date')} required data-testid="bulk-submission-date" />
              </label>
              <label className={labelClass}>
                Revision sent
                <input className={inputClass} value={form.rev} onChange={update('rev')} placeholder="e.g. R01" />
              </label>
              <label className={labelClass}>
                Sent by
                <input className={inputClass} value={form.by} onChange={update('by')} required data-testid="bulk-submission-by" />
              </label>
              <label className={`${labelClass} sm:col-span-2`}>
                PDF link
                <input className={inputClass} value={form.pdf} onChange={update('pdf')} placeholder="Optional — link to the transmittal PDF" />
              </label>
              <label className={`${labelClass} sm:col-span-2`}>
                Consultant reply
                <select className={inputClass} value={form.reply} onChange={update('reply')} data-testid="bulk-submission-reply">
                  {REPLIES.map((reply) => <option key={reply} value={reply}>{REPLY_LABELS[reply]}</option>)}
                </select>
                <span className="mt-1 block text-[11.5px] font-normal text-ink-3">{REPLY_EFFECT[form.reply]}</span>
              </label>
              <label className={`${labelClass} sm:col-span-2`}>
                Comments
                <textarea className={cn(inputClass, 'h-16 resize-y py-2')} value={form.comments} onChange={update('comments')} placeholder="Optional — transmittal number, consultant comments…" />
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-2 px-5 py-3">
            <p className="min-w-0 text-[12.5px] font-semibold text-status-red-ink" role={error ? 'alert' : undefined}>
              {error || (picking ? <span className="font-medium text-ink-3">{fmt(chosen.length)} {chosen.length === 1 ? 'drawing' : 'drawings'} picked{names.length ? ` · ${names.join(', ')}${more > 0 ? ` +${more}` : ''}` : ''}</span> : '')}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving || !chosen.length} data-testid="save-bulk-submission-btn">
                <Send aria-hidden="true" /> {saving ? 'Logging…' : picking ? `Log for ${fmt(chosen.length)}` : 'Log submission'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
